import type { CartApiResponse, FreeGiftConfig, ShopifyCartJson } from '../types';
import { formatCartState } from './formatCartState';
import appStore from './state';

const CART_SECTIONS = 'cart-drawer,giftbox-modal';

let isSyncingFreeGift = false;
let isRefreshingCart = false;
/** One-shot: refresh sections after cart-item enter anims when add ran with drawer open. */
let pendingPostEnterCartRefresh = false;

export function markPendingPostEnterCartRefresh(): void {
  pendingPostEnterCartRefresh = true;
}

export function consumePendingPostEnterCartRefresh(): boolean {
  if (!pendingPostEnterCartRefresh) return false;
  pendingPostEnterCartRefresh = false;
  return true;
}

export function getFreeGiftConfig(root: ParentNode = document): FreeGiftConfig | null {
  const configEl = root.querySelector('[data-ref="free-gift-config"]');
  if (!configEl?.textContent) return null;

  try {
    const parsed = JSON.parse(configEl.textContent) as FreeGiftConfig;
    if (!parsed.variantId || parsed.threshold <= 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

function getConfigFromDrawerSection(sections?: Record<string, string>): FreeGiftConfig | null {
  const drawerHtml = sections?.['cart-drawer'];
  if (!drawerHtml) return getFreeGiftConfig();

  const drawerRoot = new DOMParser().parseFromString(drawerHtml, 'text/html');
  return getFreeGiftConfig(drawerRoot) ?? getFreeGiftConfig();
}

function isGiftLine(item: ShopifyCartJson['items'][number], config: FreeGiftConfig): boolean {
  if (item.properties?._free_gift != null && item.properties._free_gift !== '') return true;
  return item.variant_id === config.variantId;
}

function getGiftcardsValue(cart: ShopifyCartJson): number {
  return cart.items.reduce((total, item) => {
    if (item.gift_card) return total + item.final_line_price;
    return total;
  }, 0);
}

function getEligibleSubtotal(cart: ShopifyCartJson, config: FreeGiftConfig): number {
  const giftLine = cart.items.find((item) => isGiftLine(item, config));
  const giftLinePrice = giftLine?.final_line_price ?? 0;

  return Math.max(0, cart.total_price - getGiftcardsValue(cart) - giftLinePrice);
}

function shouldHaveGift(cart: ShopifyCartJson, config: FreeGiftConfig): boolean {
  if (!config.enabled || !config.available) return false;
  return getEligibleSubtotal(cart, config) >= config.threshold;
}

async function fetchCartJson(): Promise<ShopifyCartJson> {
  // @ts-expect-error Shopify is injected on the storefront
  const response = await fetch(`${window.Shopify.routes.root}cart.js`);
  if (!response.ok) throw new Error(`Cart fetch failed (${response.status})`);
  return response.json() as Promise<ShopifyCartJson>;
}

async function addGift(config: FreeGiftConfig): Promise<CartApiResponse | null> {
  // @ts-expect-error Shopify is injected on the storefront
  const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [
        {
          id: config.variantId,
          quantity: 1,
          properties: { _free_gift: '1' },
        },
      ],
      sections: CART_SECTIONS,
    }),
  });

  if (response.status === 422) return null;
  if (!response.ok) throw new Error(`Cart add failed (${response.status})`);
  return response.json() as Promise<CartApiResponse>;
}

async function removeGiftLine(key: string): Promise<CartApiResponse> {
  // @ts-expect-error Shopify is injected on the storefront
  const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: key,
      quantity: 0,
      sections: CART_SECTIONS,
    }),
  });

  if (!response.ok) throw new Error(`Cart change failed (${response.status})`);
  return response.json() as Promise<CartApiResponse>;
}

export async function syncFreeGift(cartResponse?: CartApiResponse): Promise<CartApiResponse | null> {
  if (isSyncingFreeGift) return cartResponse ?? null;

  isSyncingFreeGift = true;

  try {
    const config = getConfigFromDrawerSection(cartResponse?.sections) ?? getFreeGiftConfig();
    if (!config) {
      const cart = cartResponse?.items ? (cartResponse as ShopifyCartJson) : await fetchCartJson();
      const orphanGift = cart.items.find((item) => item.properties?._free_gift);
      if (!orphanGift) return cartResponse ?? null;
      return removeGiftLine(orphanGift.key);
    }

    let cart: ShopifyCartJson = cartResponse?.items ? (cartResponse as ShopifyCartJson) : await fetchCartJson();

    const giftLines = cart.items.filter((item) => isGiftLine(item, config));

    // If the free gift is no longer available, remove any existing gift lines
    // from the cart (even if they were previously added).
    if (!config.available) {
      if (giftLines.length === 0) return cartResponse ?? null;

      let result: CartApiResponse = cart;
      for (const line of giftLines) {
        result = await removeGiftLine(line.key);
        cart = result as ShopifyCartJson;
      }
      return result;
    }

    const wantsGift = shouldHaveGift(cart, config);

    if (!wantsGift) {
      if (giftLines.length === 0) return cartResponse ?? null;

      let result: CartApiResponse = cart;
      for (const line of giftLines) {
        result = await removeGiftLine(line.key);
        cart = result as ShopifyCartJson;
      }
      return result;
    }

    if (giftLines.length > 0) {
      const primaryGift = giftLines.find((line) => line.properties?._free_gift) ?? giftLines[0];
      let result: CartApiResponse | null = cartResponse ?? null;

      if (primaryGift.quantity !== 1) {
        result = await removeGiftLine(primaryGift.key);
        result = (await addGift(config)) ?? result;
        return result;
      }

      for (const extraLine of giftLines.filter((line) => line.key !== primaryGift.key)) {
        result = await removeGiftLine(extraLine.key);
      }

      return result ?? cartResponse ?? null;
    }

    return (await addGift(config)) ?? cartResponse ?? null;
  } catch (error) {
    console.error(error);
    return cartResponse ?? null;
  } finally {
    isSyncingFreeGift = false;
  }
}

export async function initFreeGiftSync(): Promise<void> {
  try {
    const cart = await fetchCartJson();
    const synced = await syncFreeGift(cart);
    if (!synced?.sections) return;

    appStore.actions.setCart(formatCartState(synced));
  } catch (error) {
    console.error(error);
  }
}

async function fetchCartSections(): Promise<Record<string, string>> {
  // @ts-expect-error Shopify is injected on the storefront
  const url = new URL(window.Shopify.routes.root, window.location.origin);
  url.searchParams.set('sections', CART_SECTIONS);

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(`Cart sections fetch failed (${response.status})`);
  }

  return response.json() as Promise<Record<string, string>>;
}

/**
 * Full cart refresh after bfcache / back-forward (e.g. Back from checkout).
 * Always ends with fresh cart-drawer + giftbox-modal HTML so the UI matches
 * the live cart, even when syncFreeGift makes no gift-line changes.
 */
export async function refreshCartFromServer(): Promise<void> {
  if (isRefreshingCart) return;
  isRefreshingCart = true;

  try {
    const cart = await fetchCartJson();
    const synced = await syncFreeGift(cart);

    if (synced?.sections?.['cart-drawer']) {
      appStore.actions.setCart(formatCartState(synced));
      return;
    }

    const sections = await fetchCartSections();

    appStore.actions.setCart(
      formatCartState({
        ...(synced ?? cart),
        sections,
      }),
    );
  } catch (error) {
    console.error(error);
  } finally {
    isRefreshingCart = false;
  }
}
